import { FC, ReactNode, useEffect } from 'react'
import { useRouter } from 'next/router'
import {
  GrowthBook,
  GrowthBookProvider,
  Experiment,
  Result,
  LocalStorageStickyBucketService,
} from '@growthbook/growthbook-react'
import { autoAttributesPlugin } from '@growthbook/growthbook/plugins'

import { posthog } from 'posthog-js'

import appConfig from '~/config/app'
import authService from '~/services/auth.service'
import useCurrentUser from '~/hooks/useCurrentUser'
import { useAppSelector } from '~/redux/hooks'
import { selectSubscriptionStatus, selectSubscriptionApplicable } from '~/redux/slices/user'
import { useGetUserLocationInfoFromIpQuery } from '~/redux/apis/geo-location-api'

import { growthbookTrackingCallback } from './growthbook.logger'

const isBrowserEnvironment = typeof window !== 'undefined'

const growthbook = new GrowthBook({
  ...appConfig.growthbook,
  trackingCallback: async (experiment: Experiment<any>, result: Result<any>): Promise<void> => {
    await growthbookTrackingCallback(experiment.key, result.variationId)
  },
  // autoAttributesPlugin writes a persistent `gb_id` cookie with no consent gate, so the school
  // build runs without it. Flag evaluation still works — it just loses the auto-collected
  // URL/device/UTM attributes.
  plugins:
    isBrowserEnvironment && !appConfig.isSchoolPlatform ? [autoAttributesPlugin({ uuidKey: 'gb_id' })] : undefined,
  stickyBucketService: new LocalStorageStickyBucketService(),
})

const updateGrowthBookURL = (): void => {
  growthbook.setURL(window.location.href)
}

const GrowthBookProviderWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const router = useRouter()
  const isLoggedIn = authService.isAuthenticated()
  const user = useCurrentUser()

  const subscription = useAppSelector(selectSubscriptionStatus)
  const isSubscriptionApplicable = useAppSelector(selectSubscriptionApplicable)

  const geoLocation = useGetUserLocationInfoFromIpQuery()

  const posthogDistinctId = appConfig.isSchoolPlatform ? undefined : posthog.get_distinct_id()

  let gaClientId: undefined | string
  // GA is not loaded in the school platform, and this cookie is the join key between the analytics
  // identities — don't bridge it into GrowthBook.
  if (isBrowserEnvironment && !appConfig.isSchoolPlatform) {
    gaClientId = document?.cookie
      .match(/_ga=(.+?);/)?.[1]
      .split('.')
      .slice(-2)
      .join('.')
  }

  useEffect(() => {
    // GrowthBook is not a sub-processor for the school platform. `init()` is the only call that
    // fetches from cdn.growthbook.io, so skipping it leaves the SDK inert — every isOn() returns
    // false. ⚠ The provider must stay mounted anyway: useGrowthBook() throws without it, which
    // would break the landing page and curriculum, both kept routes.
    if (appConfig.isSchoolPlatform) return

    growthbook.init({ streaming: true })
  }, [])

  useEffect(() => {
    // Subscribe to route change events
    router.events.on('routeChangeComplete', updateGrowthBookURL)
    return () => router.events.off('routeChangeComplete', updateGrowthBookURL)
  }, [router])

  useEffect(() => {
    // Nothing evaluates flags in the school platform, so don't hand the SDK a user id, role, country
    // or subscription state either.
    if (appConfig.isSchoolPlatform) return

    growthbook.setAttributes({
      gaClientId,
      posthogDistinctId,
      userId: user?.id,
      userRole: user?.role,
      isLoggedIn,
      countryCode: geoLocation?.data?.countryCode,
      subscription,
      isSubscriptionApplicable,
    })
  }, [
    user?.id,
    user?.role,
    geoLocation?.data?.countryCode,
    subscription,
    isSubscriptionApplicable,
    isLoggedIn,
    gaClientId,
    posthogDistinctId,
  ])

  return <GrowthBookProvider growthbook={growthbook}>{children}</GrowthBookProvider>
}

export { GrowthBookProviderWrapper }
